import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Calendar,
  Building2,
  FileText,
  Users,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Target,
  ExternalLink,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import api from '../lib/api';
import Button from '@shared/ui/Button';
import PageShell from '../components/PageShell';
import { formatDate } from '../utils/dateUtils';

// Types
interface LobbyistBill {
  id: number;
  lobbyist_name: string;
  lobbyist_last_name: string;
  lobbyist_first_name: string;
  lobbyist_firm_name: string;
  lobbyist_zip: string;
  primary_lobbyist_id: string;
  annual_lobbyist_registration_id: string;
  bill_number: string;
  bill_information: string;
  client_name: string;
  client_zip: string;
  position: string;
  report_month: string;
  report_due_date: string;
  fiscal_year: string;
  run_date: string;
  created_at: string;
  updated_at: string;
  related_bills?: RelatedBill[];
}

interface RelatedBill {
  id: number;
  bill_number: string;
  bill_information: string;
  client_name: string;
  position: string;
  report_due_date: string;
}

const LobbyistBillDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [bill, setBill] = useState<LobbyistBill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchBill(id);
    }
  }, [id]);

  const fetchBill = async (billId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/lobbyist-bills/${billId}`);
      
      if (response.data.success) {
        setBill(response.data.data);
      } else {
        setError('Failed to fetch bill details');
      }
    } catch (error: any) {
      console.error('Error fetching bill:', error);
      if (error.response?.status === 404) {
        setError('Lobbyist bill not found');
      } else {
        setError('Failed to fetch bill details');
      }
    } finally {
      setLoading(false);
    }
  };

  const getPositionColor = (position: string) => {
    switch (position?.toLowerCase()) {
      case 'supporting':
        return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100';
      case 'opposing':
        return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100';
      case 'monitoring':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100';
      case 'amending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100';
      default:
        return 'bg-background-tertiary text-neutral-800 dark:bg-background-dark-secondary dark:text-text-dark-primary';
    }
  };

  const getPositionIcon = (position: string) => {
    switch (position?.toLowerCase()) {
      case 'supporting':
        return <CheckCircle className="w-5 h-5" />;
      case 'opposing':
        return <XCircle className="w-5 h-5" />;
      case 'monitoring':
        return <Eye className="w-5 h-5" />;
      case 'amending':
        return <Target className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background dark:bg-background-dark-primary">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-text-dark-primary mb-2">
              Error Loading Bill
            </h1>
            <p className="text-neutral-600 dark:text-text-dark-tertiary mb-6">{error}</p>
            <div className="space-x-4">
              <Button
                onClick={() => window.location.reload()}
                variant="secondary"
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!bill) {
    return null;
  }

  return (
    <PageShell title={bill.bill_number} description={bill.bill_information}>
      {/* min-h-screen and the page background are Layout's job, not this page's. */}
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Position and period stay — they are data, not the page title. */}
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getPositionColor(bill.position)}`}>
                  {getPositionIcon(bill.position)}
                  {bill.position}
                </span>
                <span className="text-sm text-neutral-500 dark:text-text-dark-tertiary">
                  {bill.fiscal_year} • {bill.report_month}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Bill Information */}
            <div className="card">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-text-dark-primary mb-6">
                Bill Information
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                    Bill Number
                  </h3>
                  <p className="text-neutral-900 dark:text-text-dark-primary font-medium">
                    {bill.bill_number}
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                    Bill Description
                  </h3>
                  <p className="text-neutral-900 dark:text-text-dark-primary leading-relaxed">
                    {bill.bill_information}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                      Fiscal Year
                    </h3>
                    <p className="text-neutral-900 dark:text-text-dark-primary">
                      {bill.fiscal_year}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                      Report Month
                    </h3>
                    <p className="text-neutral-900 dark:text-text-dark-primary">
                      {bill.report_month}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Lobbying Details */}
            <div className="card">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-text-dark-primary mb-6">
                Lobbying Details
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                    Position
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getPositionColor(bill.position)}`}>
                      {getPositionIcon(bill.position)}
                      {bill.position}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                      Report Due Date
                    </h3>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-neutral-500" />
                      <p className="text-neutral-900 dark:text-text-dark-primary">
                        {formatDate(bill.report_due_date)}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                      Data Run Date
                    </h3>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-neutral-500" />
                      <p className="text-neutral-900 dark:text-text-dark-primary">
                        {formatDate(bill.run_date)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Related Bills */}
            {bill.related_bills && bill.related_bills.length > 0 && (
              <div className="card">
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-text-dark-primary mb-6">
                  Other Bills by {bill.lobbyist_name}
                </h2>
                
                <div className="space-y-4">
                  {bill.related_bills.map((relatedBill) => (
                    <div
                      key={relatedBill.id}
                      className="border border-border-light dark:border-border-dark-hairline rounded-lg p-4 hover:bg-background-tertiary dark:hover:bg-background-dark-secondary transition-colors cursor-pointer"
                      onClick={() => navigate(`/lobbyist-bills/${relatedBill.id}`)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-medium text-neutral-900 dark:text-text-dark-primary mb-1">
                            {relatedBill.bill_number}
                          </h3>
                          <p className="text-sm text-neutral-600 dark:text-text-dark-tertiary mb-2 line-clamp-2">
                            {relatedBill.bill_information}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-neutral-500">
                            <span>Client: {relatedBill.client_name}</span>
                            <span className={`px-2 py-1 rounded ${getPositionColor(relatedBill.position)}`}>
                              {relatedBill.position}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs text-neutral-500 ml-4">
                          {formatDate(relatedBill.report_due_date)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Lobbyist Information */}
            <div className="card">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-text-dark-primary mb-4">
                Lobbyist Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-neutral-500" />
                    <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary">
                      Name
                    </h3>
                  </div>
                  <p className="text-neutral-900 dark:text-text-dark-primary font-medium">
                    {bill.lobbyist_name}
                  </p>
                </div>

                {bill.lobbyist_firm_name && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4 text-neutral-500" />
                      <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary">
                        Firm
                      </h3>
                    </div>
                    <p className="text-neutral-900 dark:text-text-dark-primary">
                      {bill.lobbyist_firm_name}
                    </p>
                  </div>
                )}

                {bill.lobbyist_zip && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-neutral-500" />
                      <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary">
                        Zip Code
                      </h3>
                    </div>
                    <p className="text-neutral-900 dark:text-text-dark-primary">
                      {bill.lobbyist_zip}
                    </p>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary mb-2">
                    Registration ID
                  </h3>
                  <p className="text-xs text-neutral-600 dark:text-text-dark-tertiary font-mono">
                    {bill.annual_lobbyist_registration_id}
                  </p>
                </div>
              </div>
            </div>

            {/* Client Information */}
            <div className="card">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-text-dark-primary mb-4">
                Client Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-neutral-500" />
                    <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary">
                      Client Name
                    </h3>
                  </div>
                  <p className="text-neutral-900 dark:text-text-dark-primary font-medium">
                    {bill.client_name}
                  </p>
                </div>

                {bill.client_zip && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-neutral-500" />
                      <h3 className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary">
                        Zip Code
                      </h3>
                    </div>
                    <p className="text-neutral-900 dark:text-text-dark-primary">
                      {bill.client_zip}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="card">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-text-dark-primary mb-4">
                Actions
              </h2>
              
              <div className="space-y-3">
                <Link
                  to="/lobbyists"
                  className="flex items-center gap-2 w-full p-3 text-left border border-border-light dark:border-border-dark-hairline rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-secondary transition-colors"
                >
                  <Users className="w-4 h-4 text-neutral-500" />
                  <span className="text-sm text-neutral-700 dark:text-text-dark-secondary">
                    View All Lobbyists
                  </span>
                  <ExternalLink className="w-4 h-4 text-neutral-500 ml-auto" />
                </Link>
                
                <Link
                  to="/bills"
                  className="flex items-center gap-2 w-full p-3 text-left border border-border-light dark:border-border-dark-hairline rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-secondary transition-colors"
                >
                  <FileText className="w-4 h-4 text-neutral-500" />
                  <span className="text-sm text-neutral-700 dark:text-text-dark-secondary">
                    View All Bills
                  </span>
                  <ExternalLink className="w-4 h-4 text-neutral-500 ml-auto" />
                </Link>
              </div>
            </div>

            {/* Metadata */}
            <div className="card">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-text-dark-primary mb-4">
                Record Information
              </h2>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-text-dark-tertiary">Created:</span>
                  <span className="text-neutral-900 dark:text-text-dark-primary">
                    {formatDate(bill.created_at)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-text-dark-tertiary">Updated:</span>
                  <span className="text-neutral-900 dark:text-text-dark-primary">
                    {formatDate(bill.updated_at)}
                  </span>
                </div>
                <div className="pt-2 border-t border-border-light dark:border-border-dark-hairline">
                  <span className="text-xs text-neutral-500 dark:text-text-dark-tertiary">
                    Record ID: {bill.id}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default LobbyistBillDetailPage;